import React, { useContext, useEffect, useState } from 'react';

import { HttpMethods, FetchContext, ApiRoutes } from './FetchContext';
import { useAggregatedState } from '../../helpers';
import { InsightContext } from './';
import { useHistory, useLocation } from 'react-router-dom';
import { NavRoutes } from './../contexts';

export const UserContext = React.createContext(null);

const { Provider } = UserContext;
export const UserProvider = ({ ...props }) => {
    const { trackError } = useContext(InsightContext);
    const { fetchAuthorized } = useContext(FetchContext);
    const history = useHistory();

    const [userInfo, setUserInfo] = useState({ externalKey: null, roles: [] });
    
    const [isLoadingInfo, setIsLoadingInfo] = useState(false);
    const getUserInfoAsync = async () => {
        try {
            setIsLoadingInfo(true);

            const result = await fetchAuthorized(
                ApiRoutes.User.GetMe(),
                { method: HttpMethods.GET }
            );
            
            const userInfo = await result.json();
            setUserInfo(userInfo);
            setIsLoadingInfo(false);
        }
        catch(e) {
            console.debug(e);
            trackError(e);
            setUserInfo({ externalKey: "not-allowed", roles: [] });
            setIsLoadingInfo(false);          
        }
    }

    const isUserInRole = (roleName) => {
        if(userInfo === null || userInfo.roles === null) return false;
        if(userInfo.roles.length === 0) return false;
        const role = userInfo.roles.find(x => x.roleName === roleName);
        return role !== null && role !== undefined;
    }

    const isUserInRoles = (roleNames) => {
        if(userInfo === null || userInfo.roles === null) return false;
        if(userInfo.roles.length === 0) return false;
        const role = userInfo.roles.find(x => roleNames.find(n => x.roleName === n));
        return role !== null && role !== undefined;
    }

    const activateBudgetYearAsync = async (data) => {
        try {            
            const activateYearApi = await fetchAuthorized(ApiRoutes.Budget.ActivateBudgetYear(),
            {                    
                method: HttpMethods.POST,
                body: JSON.stringify(data),
                collectErrorDetails: true
            }); 
    
            const activatedYear = await activateYearApi.json();
          }
    
          catch (ex){
            console.error(ex);
            console.error(ex.details);
          }
    }
    
    const [budgetYears, setBudgetYears] = useState(null);
    const getBudgetYearsAsync = async () => {
        try {            
            const budgetYearsApi = await fetchAuthorized(
                ApiRoutes.Budget.GetBudgetYears({ pageSize: 1000 }),
                { method: HttpMethods.GET }
            ); 
    
            const allYears = await budgetYearsApi.json();

            if (allYears){
                allYears.items.sort((a, b) =>
                   a.observedYear - b.observedYear
               );
            }

            setBudgetYears(allYears);

            if (allYears && allYears.items && allYears.items.length > 0){
                let activeYears = [];
                let activeYearObject = null;
                allYears.items.map(year => {
                    if (year.isActive){
                        activeYearObject = year;
                        activeYears.push(year.budgetYearId);
                    }
                });
                
                //default year is the first active one...
                if (activeYears.length > 0)
                    getBudgetSessionAsync(activeYears[0]);
                //...or is the first year in general
                else if(allYears.items.length > 0)
                    getBudgetSessionAsync(allYears.items[0].budgetYearId);
            }
        }
    
        catch (ex){
            console.error(ex);
            console.error(ex.details);
        }
    }

    const [budget, setBudget] = useState(null);
    const [layoutBudget, setLayoutBudget] = useState(null);
    const getBudgetSessionAsync = async (data, isNewSelection) => {
        try {            
            const sessionApi = await fetchAuthorized(
                ApiRoutes.Budget.GetBudgetYear({ budgetYearId: data }),
                { method: HttpMethods.GET }
            ); 
    
            const currentBudget = await sessionApi.json();

            //first time
            if (!budget){
                setLayoutBudget(currentBudget);
                setBudget(currentBudget);
            }

            else {
                if (isNewSelection)
                    setLayoutBudget(currentBudget);
                else
                    setBudget(currentBudget);
            }

            if (isNewSelection === "homepage")
                history.replace(NavRoutes.MyProjectsPage);
        }
    
        catch (ex){
            console.error(ex);
            console.error(ex.details);
        }
    }

    const [allReports, setAllReports] = useState(null);
    const getAllReportsAsync = async () => {
        try {            
            const reportsApi = await fetchAuthorized(ApiRoutes.Report.GetReportLinks({ pageSize: 1000 }),
            {                    
                method: HttpMethods.GET
            }); 
    
            const report = await reportsApi.json();
            setAllReports(report);
          }
    
          catch (ex){
            console.error(ex);
            console.error(ex.details);
          }
    }

    const [updatedReport, setUpdatedReport] = useState(null);
    const updateReportAsync = async (data) => {
        try {            
            const reportApi = await fetchAuthorized(ApiRoutes.Report.UpdateReportLink(),
            {                    
                method: HttpMethods.POST,
                body: JSON.stringify(data),
                collectErrorDetails: true
            }); 
    
            const currentReport = await reportApi.json();
            setUpdatedReport(currentReport);
          }
    
          catch (ex){
            console.error(ex);
            console.error(ex.details);
          }
    }

    const resetUpdate = () => {
        setUpdatedReport(null);
    }

    const [shouldMenuClose, setShouldMenuClose] = useState(null);
    const closeMenuRequest = menuStatus => {
        setShouldMenuClose(menuStatus);
    }

    const [selectedYear, setSelectedYear] = useState(null);

    const [globalSettings, setGlobalSettings] = useState(null);
    const [readOnlyMode, setReadOnlyMode] = useState(null);
    const [isLoadingGlobalSettings, setIsLoadingGlobalSettings] = useState(false);
    const getGlobalSettingsAsync = async () => {
        try {
            setIsLoadingGlobalSettings(true);

            const globalSettingsApi = await fetchAuthorized(
                ApiRoutes.GlobalSettings.GetGlobalSettings(),
                { method: HttpMethods.GET }
            ); 
    
            const response = await globalSettingsApi.json();

            if (response && response.items.length > 0)
                setReadOnlyMode(response.items.filter((x) => x.globalSettingDefinition === 1)[0])

            setGlobalSettings(response.items)
            setIsLoadingGlobalSettings(false);
        }
    
        catch (ex){
            setIsLoadingGlobalSettings(false);
            console.error(ex);
            console.error(ex.details);
        }
    }

    return (
        <Provider value={{
            userInfo,
            isLoadingInfo,
            getUserInfoAsync,
            isUserInRole,
            isUserInRoles,
            activateBudgetYearAsync,
            getBudgetYearsAsync,
            budgetYears,
            getBudgetSessionAsync,
            budget,
            layoutBudget,
            getAllReportsAsync,
            allReports,
            updateReportAsync,
            updatedReport,
            resetUpdate,
            closeMenuRequest,
            shouldMenuClose,
            selectedYear,
            setSelectedYear,
            getGlobalSettingsAsync,
            isLoadingGlobalSettings,
            globalSettings,
            readOnlyMode
        }}>
            {props.children}
        </Provider>
    );
}